import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  Stack,
  Typography,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Popover,
  Grid,
  Button,
  ButtonGroup,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatStrikethrough,
  FormatColorText,
  FormatColorFill,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  FormatListBulleted,
  FormatListNumbered,
  FormatIndentIncrease,
  FormatIndentDecrease,
  ArrowDropDown,
} from '@mui/icons-material';
import {
  FORMAT_ELEMENT_COMMAND,
  $getSelection,
  $isRangeSelection,
  $createRangeSelection,
  $setSelection,
} from 'lexical';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import useThemeStyles from '../../../../../../../hooks/useThemeStyles';
import { useEditorContext } from '../../../../../../../contexts/EditorContext';
import {
  FONT_SIZE_COMMAND,
  FONT_FAMILY_COMMAND,
  handleColorCommand,
  handleHighlightCommand,
} from '../../../../../../Editor/Components/Plugins/StyledTextCommands';
import {
  INCREASE_INDENT_COMMAND,
  DECREASE_INDENT_COMMAND,
} from '../../../../../../Editor/Components/Plugins/Indentation/IndentPlugin';
import fontsConfig from '../../../../../../../utils/fonts.json';
import { getFontFamilyValue } from '../../../../../../../utils/fontUtils';

const ActionBar = ({ onFormatChange = undefined, actionBarId = undefined }) => {
  const { colors, components } = useThemeStyles();
  const { formattingState, updateFormatting, activeEditor } = useEditorContext();

  /** Snapshot of Lexical range selection before toolbar/popover steals focus (fixes 1st-click misses). */
  const toolbarSelectionRef = useRef(null);

  const captureToolbarSelection = useCallback(() => {
    const ed = activeEditor?.editor;
    if (!ed) return;
    ed.getEditorState().read(() => {
      const sel = $getSelection();
      if ($isRangeSelection(sel) && !sel.isCollapsed()) {
        toolbarSelectionRef.current = {
          anchorKey: sel.anchor.key,
          anchorOffset: sel.anchor.offset,
          focusKey: sel.focus.key,
          focusOffset: sel.focus.offset,
        };
      }
    });
  }, [activeEditor]);

  const restoreToolbarSelectionInUpdate = useCallback(() => {
    const snap = toolbarSelectionRef.current;
    if (!snap) return false;
    try {
      const range = $createRangeSelection();
      range.anchor.set(snap.anchorKey, snap.anchorOffset, 'text');
      range.focus.set(snap.focusKey, snap.focusOffset, 'text');
      $setSelection(range);
      return true;
    } catch {
      return false;
    }
  }, []);

  // Local state for popovers
  const [fontColorAnchor, setFontColorAnchor] = useState(null);
  const [highlightColorAnchor, setHighlightColorAnchor] = useState(null);

  // Use formatting state from context
  const {
    fontFamily,
    fontSize,
    bold: isBold,
    italic: isItalic,
    underline: isUnderline,
    strikethrough: isStrikethrough,
    fontColor,
    highlightColor,
    textAlign,
    lineSpacing,
    listType,
    indentLevel,
  } = formattingState;

  // Font families are now loaded directly from fontsConfig in the dropdown

  // Common font sizes
  const fontSizes = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 72];

  // Line spacing options
  const lineSpacingOptions = [
    { value: 1, label: '1.0' },
    { value: 1.15, label: '1.15' },
    { value: 1.5, label: '1.5' },
    { value: 2, label: '2.0' },
    { value: 2.5, label: '2.5' },
    { value: 3, label: '3.0' },
  ];

  // Color palette
  const colorPalette = [
    '#000000',
    '#FFFFFF',
    '#FF0000',
    '#00FF00',
    '#0000FF',
    '#FFFF00',
    '#FF00FF',
    '#00FFFF',
    '#800000',
    '#008000',
    '#000080',
    '#808080',
    '#FFA500',
    '#FFC0CB',
    '#A52A2A',
    '#808080',
    '#FFFF00',
    '#00FF00',
  ];

  // Formatting handlers
  const handleFontFamilyChange = (event) => {
    const fontName = event.target.value;
    const fontFamilyValue = getFontFamilyValue(fontName);

    // Dispatch font family command to active editor with the proper CSS value
    if (activeEditor?.editor) {
      activeEditor.editor.dispatchCommand(FONT_FAMILY_COMMAND, fontFamilyValue);
    }

    updateFormatting({ fontFamily: fontName });
    if (onFormatChange) {
      onFormatChange({ fontFamily: fontName });
    }
  };

  const handleFontSizeChange = (event) => {
    const newFontSize = event.target.value;

    // Dispatch font size command to active editor
    if (activeEditor?.editor) {
      activeEditor.editor.dispatchCommand(FONT_SIZE_COMMAND, newFontSize);
    }

    updateFormatting({ fontSize: newFontSize });
    if (onFormatChange) {
      onFormatChange({ fontSize: newFontSize });
    }
  };

  const handleBoldToggle = () => {
    const newBold = !isBold;
    updateFormatting({ bold: newBold });
    if (onFormatChange) {
      onFormatChange({ bold: newBold });
    }
  };

  const handleItalicToggle = () => {
    const newItalic = !isItalic;
    updateFormatting({ italic: newItalic });
    if (onFormatChange) {
      onFormatChange({ italic: newItalic });
    }
  };

  const handleUnderlineToggle = () => {
    const newUnderline = !isUnderline;
    updateFormatting({ underline: newUnderline });
    if (onFormatChange) {
      onFormatChange({ underline: newUnderline });
    }
  };

  const handleStrikethroughToggle = () => {
    const newStrikethrough = !isStrikethrough;
    updateFormatting({ strikethrough: newStrikethrough });
    if (onFormatChange) {
      onFormatChange({ strikethrough: newStrikethrough });
    }
  };

  const handleFontColorChange = (color) => {
    const ed = activeEditor?.editor;
    if (ed) {
      ed.update(() => {
        restoreToolbarSelectionInUpdate();
        handleColorCommand(color);
      });
    }

    updateFormatting({ fontColor: color });
    setFontColorAnchor(null);
    if (onFormatChange) {
      onFormatChange({ fontColor: color });
    }
  };

  const handleHighlightColorChange = (color) => {
    const ed = activeEditor?.editor;
    if (ed) {
      const root = ed.getRootElement();
      if (root) {
        root.focus({ preventScroll: true });
      }
      ed.update(() => {
        restoreToolbarSelectionInUpdate();
        // Clear first, then apply — matches what works when users "toggle off then on" and avoids
        // inconsistent state when toolbar default suggested highlight was already active.
        handleHighlightCommand(null);
        restoreToolbarSelectionInUpdate();
        handleHighlightCommand(color);
      });
    }

    updateFormatting({ highlightColor: color });
    setHighlightColorAnchor(null);
    if (onFormatChange) {
      onFormatChange({ highlightColor: color });
    }
  };

  const handleFontColorClick = (event) => {
    setFontColorAnchor(event.currentTarget);
  };

  const handleFontColorDropdownClick = (event) => {
    setFontColorAnchor(event.currentTarget);
  };

  const handleHighlightColorClick = (event) => {
    setHighlightColorAnchor(event.currentTarget);
  };

  const handleHighlightColorDropdownClick = (event) => {
    setHighlightColorAnchor(event.currentTarget);
  };

  // Text alignment handlers
  const handleTextAlign = (alignment) => {
    // Dispatch format element command to active editor
    if (activeEditor?.editor) {
      activeEditor.editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, alignment);
    }

    updateFormatting({ textAlign: alignment });
    if (onFormatChange) {
      onFormatChange({ textAlign: alignment });
    }
  };

  // Line spacing handler
  const handleLineSpacingChange = (event) => {
    const newLineSpacing = event.target.value;
    updateFormatting({ lineSpacing: newLineSpacing });
    if (onFormatChange) {
      onFormatChange({ lineSpacing: newLineSpacing });
    }
  };

  // List formatting handlers
  const handleListType = (type) => {
    // Dispatch list command to active editor
    if (activeEditor?.editor) {
      if (type === 'bullet') {
        activeEditor.editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
      } else if (type === 'numbered') {
        activeEditor.editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
      } else if (type === 'none') {
        activeEditor.editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      }
    }

    updateFormatting({ listType: type });
    if (onFormatChange) {
      onFormatChange({ listType: type });
    }
  };

  // Indent handlers
  const handleIncreaseIndent = () => {
    // Dispatch increase indent command to active editor
    if (activeEditor?.editor) {
      activeEditor.editor.dispatchCommand(INCREASE_INDENT_COMMAND, undefined);
    }
    const newIndentLevel = indentLevel + 1;
    updateFormatting({ indentLevel: newIndentLevel });
    if (onFormatChange) {
      onFormatChange({ indentLevel: newIndentLevel });
    }
  };

  const handleDecreaseIndent = () => {
    // Dispatch decrease indent command to active editor
    if (activeEditor?.editor) {
      activeEditor.editor.dispatchCommand(DECREASE_INDENT_COMMAND, undefined);
    }
    const newIndentLevel = Math.max(0, indentLevel - 1);
    updateFormatting({ indentLevel: newIndentLevel });
    if (onFormatChange) {
      onFormatChange({ indentLevel: newIndentLevel });
    }
  };

  return (
    <Box
      id={actionBarId}
      onMouseDown={(e) => {
        // Keep Lexical selection when toggling toolbar buttons (don't steal focus from the editor).
        // Exclude font/line selects so dropdowns still open and focus normally.
        const tag = e.target?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        const inMenuOrPopover = e.target?.closest?.(
          '.MuiPopover-root, .MuiMenu-root, .MuiModal-root, [role="listbox"], [role="presentation"]'
        );
        if (inMenuOrPopover) return;
        const inSelect = e.target?.closest?.('.MuiFormControl-root .MuiSelect-select');
        if (inSelect) return;
        const isFormattingButton = e.target?.closest?.(
          'button.MuiIconButton-root, button.MuiButton-root, .MuiButtonGroup-root button'
        );
        if (!isFormattingButton) return;
        e.preventDefault();
      }}
      sx={{
        backgroundColor: colors.sidebar.background,
        border: components.card.border,
        borderRadius: 2,
        p: 1.5,
        mb: 2,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        // Ensure the background is opaque when sticky
        backdropFilter: 'blur(8px)',
        // Add a subtle shadow when sticky to indicate it's floating
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {/* Font Family Selector */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={fontFamily}
            onChange={handleFontFamilyChange}
            sx={{
              fontSize: '0.875rem',
              color: colors.text.primary,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.border.subtle,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.border.primary,
              },
            }}
          >
            {fontsConfig.supportedFonts.map((font) => (
              <MenuItem key={font.name} value={font.name} sx={{ fontSize: '0.875rem' }}>
                <span style={{ fontFamily: font.family }}>{font.name}</span>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Font Size Selector */}
        <FormControl size="small" sx={{ minWidth: 60 }}>
          <Select
            value={fontSize}
            onChange={handleFontSizeChange}
            sx={{
              fontSize: '0.875rem',
              color: colors.text.primary,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.border.subtle,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.border.primary,
              },
            }}
          >
            {fontSizes.map((size) => (
              <MenuItem key={size} value={`${size}px`} sx={{ fontSize: '0.875rem' }}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Divider */}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Bold Button */}
        <Tooltip title="Bold">
          <IconButton
            size="small"
            onClick={handleBoldToggle}
            sx={{
              color: isBold ? colors.primary.main : colors.text.secondary,
              backgroundColor: isBold ? colors.interactive.hover : 'transparent',
              '&:hover': {
                backgroundColor: colors.interactive.hover,
                color: colors.text.primary,
              },
            }}
          >
            <FormatBold />
          </IconButton>
        </Tooltip>

        {/* Italic Button */}
        <Tooltip title="Italic">
          <IconButton
            size="small"
            onClick={handleItalicToggle}
            sx={{
              color: isItalic ? colors.primary.main : colors.text.secondary,
              backgroundColor: isItalic ? colors.interactive.hover : 'transparent',
              '&:hover': {
                backgroundColor: colors.interactive.hover,
                color: colors.text.primary,
              },
            }}
          >
            <FormatItalic />
          </IconButton>
        </Tooltip>

        {/* Underline Button */}
        <Tooltip title="Underline">
          <IconButton
            size="small"
            onClick={handleUnderlineToggle}
            sx={{
              color: isUnderline ? colors.primary.main : colors.text.secondary,
              backgroundColor: isUnderline ? colors.interactive.hover : 'transparent',
              '&:hover': {
                backgroundColor: colors.interactive.hover,
                color: colors.text.primary,
              },
            }}
          >
            <FormatUnderlined />
          </IconButton>
        </Tooltip>

        {/* Strikethrough Button */}
        <Tooltip title="Strikethrough">
          <IconButton
            size="small"
            onClick={handleStrikethroughToggle}
            sx={{
              color: isStrikethrough ? colors.primary.main : colors.text.secondary,
              backgroundColor: isStrikethrough ? colors.interactive.hover : 'transparent',
              '&:hover': {
                backgroundColor: colors.interactive.hover,
                color: colors.text.primary,
              },
            }}
          >
            <FormatStrikethrough />
          </IconButton>
        </Tooltip>

        {/* Divider */}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Font Color Button Group */}
        <Tooltip title="Font Color">
          <ButtonGroup size="small" variant="outlined">
            <Button
              onMouseDown={captureToolbarSelection}
              onClick={handleFontColorClick}
              sx={{
                minWidth: 'auto',
                px: 1,
                color: colors.text.secondary,
                borderColor: colors.border.subtle,
                '&:hover': {
                  backgroundColor: colors.interactive.hover,
                  borderColor: colors.border.primary,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FormatColorText />
                {fontColor && (
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: fontColor,
                      border: `1px solid ${colors.border.subtle}`,
                      borderRadius: 0.5,
                    }}
                  />
                )}
              </Box>
            </Button>
            <Button
              onMouseDown={captureToolbarSelection}
              onClick={handleFontColorDropdownClick}
              sx={{
                minWidth: 'auto',
                px: 0.5,
                color: colors.text.secondary,
                borderColor: colors.border.subtle,
                '&:hover': {
                  backgroundColor: colors.interactive.hover,
                  borderColor: colors.border.primary,
                },
              }}
            >
              <ArrowDropDown />
            </Button>
          </ButtonGroup>
        </Tooltip>

        {/* Highlight Color Button Group */}
        <Tooltip title="Highlight Color">
          <ButtonGroup size="small" variant="outlined">
            <Button
              onMouseDown={captureToolbarSelection}
              onClick={handleHighlightColorClick}
              sx={{
                minWidth: 'auto',
                px: 1,
                color: colors.text.secondary,
                borderColor: colors.border.subtle,
                '&:hover': {
                  backgroundColor: colors.interactive.hover,
                  borderColor: colors.border.primary,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FormatColorFill />
                {highlightColor && (
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: highlightColor,
                      border: `1px solid ${colors.border.subtle}`,
                      borderRadius: 0.5,
                    }}
                  />
                )}
              </Box>
            </Button>
            <Button
              onMouseDown={captureToolbarSelection}
              onClick={handleHighlightColorDropdownClick}
              sx={{
                minWidth: 'auto',
                px: 0.5,
                color: colors.text.secondary,
                borderColor: colors.border.subtle,
                '&:hover': {
                  backgroundColor: colors.interactive.hover,
                  borderColor: colors.border.primary,
                },
              }}
            >
              <ArrowDropDown />
            </Button>
          </ButtonGroup>
        </Tooltip>

        {/* Divider */}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Text Alignment Buttons */}
        <Tooltip title="Align Left">
          <IconButton
            size="small"
            onClick={() => handleTextAlign('left')}
            sx={{
              color: textAlign === 'left' ? colors.primary.main : colors.text.secondary,
              backgroundColor: textAlign === 'left' ? colors.interactive.hover : 'transparent',
              '&:hover': {
                backgroundColor: colors.interactive.hover,
                color: colors.text.primary,
              },
            }}
          >
            <FormatAlignLeft />
          </IconButton>
        </Tooltip>

        <Tooltip title="Align Center">
          <IconButton
            size="small"
            onClick={() => handleTextAlign('center')}
            sx={{
              color: textAlign === 'center' ? colors.primary.main : colors.text.secondary,
              backgroundColor: textAlign === 'center' ? colors.interactive.hover : 'transparent',
              '&:hover': {
                backgroundColor: colors.interactive.hover,
                color: colors.text.primary,
              },
            }}
          >
            <FormatAlignCenter />
          </IconButton>
        </Tooltip>

        <Tooltip title="Align Right">
          <IconButton
            size="small"
            onClick={() => handleTextAlign('right')}
            sx={{
              color: textAlign === 'right' ? colors.primary.main : colors.text.secondary,
              backgroundColor: textAlign === 'right' ? colors.interactive.hover : 'transparent',
              '&:hover': {
                backgroundColor: colors.interactive.hover,
                color: colors.text.primary,
              },
            }}
          >
            <FormatAlignRight />
          </IconButton>
        </Tooltip>

        <Tooltip title="Justify">
          <IconButton
            size="small"
            onClick={() => handleTextAlign('justify')}
            sx={{
              color: textAlign === 'justify' ? colors.primary.main : colors.text.secondary,
              backgroundColor: textAlign === 'justify' ? colors.interactive.hover : 'transparent',
              '&:hover': {
                backgroundColor: colors.interactive.hover,
                color: colors.text.primary,
              },
            }}
          >
            <FormatAlignJustify />
          </IconButton>
        </Tooltip>

        {/* Divider */}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Line Spacing Selector */}
        <FormControl size="small" sx={{ minWidth: 70 }}>
          <Select
            value={lineSpacing}
            onChange={handleLineSpacingChange}
            sx={{
              fontSize: '0.875rem',
              color: colors.text.primary,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.border.subtle,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.border.primary,
              },
            }}
          >
            {lineSpacingOptions.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.875rem' }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Divider */}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Bullet List Button */}
        <Tooltip title="Bullet List">
          <IconButton
            size="small"
            onClick={() => handleListType(listType === 'bullet' ? 'none' : 'bullet')}
            sx={{
              color: listType === 'bullet' ? colors.primary.main : colors.text.secondary,
              backgroundColor: listType === 'bullet' ? colors.interactive.hover : 'transparent',
              '&:hover': {
                backgroundColor: colors.interactive.hover,
                color: colors.text.primary,
              },
            }}
          >
            <FormatListBulleted />
          </IconButton>
        </Tooltip>

        {/* Numbered List Button */}
        <Tooltip title="Numbered List">
          <IconButton
            size="small"
            onClick={() => handleListType(listType === 'numbered' ? 'none' : 'numbered')}
            sx={{
              color: listType === 'numbered' ? colors.primary.main : colors.text.secondary,
              backgroundColor: listType === 'numbered' ? colors.interactive.hover : 'transparent',
              '&:hover': {
                backgroundColor: colors.interactive.hover,
                color: colors.text.primary,
              },
            }}
          >
            <FormatListNumbered />
          </IconButton>
        </Tooltip>

        {/* Divider */}
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Decrease Indent Button */}
        <Tooltip title="Decrease Indent">
          <IconButton
            size="small"
            onClick={handleDecreaseIndent}
            sx={{
              color: colors.text.secondary,
              '&:hover': {
                backgroundColor: colors.interactive.hover,
                color: colors.text.primary,
              },
            }}
          >
            <FormatIndentDecrease />
          </IconButton>
        </Tooltip>

        {/* Increase Indent Button */}
        <Tooltip title="Increase Indent" id="increase-indent-tooltip">
          <IconButton
            size="small"
            onClick={handleIncreaseIndent}
            sx={{
              color: colors.text.secondary,
              '&:hover': {
                backgroundColor: colors.interactive.hover,
                color: colors.text.primary,
              },
            }}
          >
            <FormatIndentIncrease />
          </IconButton>
        </Tooltip>

        {/* Font Color Popover */}
        <Popover
          open={Boolean(fontColorAnchor)}
          anchorEl={fontColorAnchor}
          onClose={() => setFontColorAnchor(null)}
          disableAutoFocus
          disableEnforceFocus
          slotProps={{
            paper: {
              onMouseDown: (e) => e.preventDefault(),
            },
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, minWidth: 200 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: colors.text.primary }}>
              Font Color
            </Typography>
            <Grid container spacing={1}>
              {colorPalette.map((color) => (
                <Grid item key={color}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: color,
                      border: `2px solid ${fontColor === color ? colors.primary.main : colors.border.subtle}`,
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: colors.primary.main,
                      },
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      captureToolbarSelection();
                    }}
                    onClick={() => handleFontColorChange(color)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Popover>

        {/* Highlight Color Popover */}
        <Popover
          open={Boolean(highlightColorAnchor)}
          anchorEl={highlightColorAnchor}
          onClose={() => setHighlightColorAnchor(null)}
          disableAutoFocus
          disableEnforceFocus
          slotProps={{
            paper: {
              onMouseDown: (e) => e.preventDefault(),
            },
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Box sx={{ p: 2, minWidth: 200 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: colors.text.primary }}>
              Highlight Color
            </Typography>
            <Grid container spacing={1}>
              {colorPalette.map((color, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Grid item key={`${color} - color - ${index}`}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: color,
                      border: `2px solid ${highlightColor === color ? colors.primary.main : colors.border.subtle}`,
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: colors.primary.main,
                      },
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      captureToolbarSelection();
                    }}
                    onClick={() => handleHighlightColorChange(color)}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Popover>
      </Stack>
    </Box>
  );
};

/* eslint-disable react/require-default-props */
ActionBar.propTypes = {
  onFormatChange: PropTypes.func,
  actionBarId: PropTypes.string,
};
/* eslint-enable react/require-default-props */

export default ActionBar;
